import { useTradeStore } from '@/stores/tradeStore'
import type {
  CreateSwapOrderRequest,
  CreateSwapOrdersRequest,
  ClosePositionsRequest,
  PrepareApiRequest,
} from '@/services/trade-v2-service/types'
import { useMyToast, type MyToastOptions } from './useMyToast'
import { i18n } from '@/i18n'
import { type Ref } from 'vue'
import { calcSpread } from '@/utils'
import { buildSymbol, DEFAULT_QUOTE } from '@/utils/quoteHelpers'
import { useCoreStore } from '@/stores/coreStore'
import type { CcxtOrderData, MarketType } from '@/utils/types'
import { useUserStore } from '@/stores/userStore'
import type { ApiKeyType } from '@/services/api-keys-v2-service/types'

export type Severities = 'error' | 'success'

export const useTrade = (
  profilesMax: Ref<number | null>,
  enabledProfiles: Ref<number[]>,
  notifications: Ref<boolean>,
) => {
  // Вызов API и обработка в тостах

  const tradeStore = useTradeStore()
  const { addToast } = useMyToast()
  const { t } = i18n.global
  const coreStore = useCoreStore()
  const userStore = useUserStore()

  const signalSeverities = {
    error: '❌ Ошибка',
    success: '✅ Успех',
  }

  const sendUserSignal = (
    title: string,
    severity: Severities,
    asset: string,
    exs: string,
    message: string,
  ) => {
    let content = ''
    if (title) {
      content += `<b>${title}</b>\n\n`
    }
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('trigger')
    currentUrl.searchParams.delete('auto')
    currentUrl.searchParams.delete('autoSl')
    currentUrl.searchParams.delete('autoTp')
    currentUrl.searchParams.delete('avg')
    currentUrl.searchParams.delete('debug')
    currentUrl.searchParams.delete('nocharts')
    const cleanUrl = currentUrl.toString()
    content += `<a href="${cleanUrl}"><b>${signalSeverities[severity]}! ${asset} ${exs}</b></a>\n\n`
    content += message
    userStore.sendSignal(content)
  }

  const _addToast = (e: MyToastOptions) => {
    if (notifications.value) {
      addToast(e)
    }
  }

  const fetchBalance = async (
    exchange: string,
    marketType: 'spot' | 'swap',
    profileNum: number,
    keyType?: ApiKeyType,
    quoteAsset?: string,
  ) => {
    if (!keyType) {
      keyType = 'cex_cex'
    }
    if (!quoteAsset) {
      quoteAsset = 'USDT'
    }
    const req = await tradeStore.fetchBalance(exchange, profileNum, marketType, keyType)
    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    const error = tradeStore.errors.balance
    const toasts = [] as { message: string; severity: Severities }[]
    if (error) {
      const errorType =
        error.code == -255
          ? 'unexceptedError'
          : (error.message.detail[0]?.type ?? 'unexceptedError')
      const errorDetail = error.message.detail[0]?.params as Array<Array<string | number>>
      toasts.push({
        message: t(`errors.${errorType}`, errorDetail),
        severity: 'error',
      })
    } else {
      const freeUsdt = Number(req?.[quoteAsset]?.free ?? 0).toFixed(2)
      toasts.push({
        message: t(`modal.createSwapOrders.msgs.fetchBalance`, {
          ex: exchange,
          freeUsdt: freeUsdt,
        }),
        severity: 'success',
      })
      console.log(toasts)
    }
    for (const toast of toasts) {
      addToast({
        title: profileTitle,
        message: toast.message,
        severity: toast.severity,
      })
    }
  }

  const cancelOrders = async (
    exBuy: string,
    exSell: string,
    marketType: MarketType,
    asset: string,
    profileNum: number,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    const data = {
      asset: asset,
      sell_asset: sellAsset,
      market_type: marketType,
      ex_buy: exBuy,
      ex_sell: exSell,
      quote_buy: quoteBuy,
      quote_sell: quoteSell,
    } as ClosePositionsRequest
    const req = await tradeStore.cancelSwapOrders(profileNum, data)
    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    const error = tradeStore.errors.cancelSwapOrders
    const toasts = [] as { message: string; severity: Severities }[]
    if (error) {
      const errorType =
        error.code == -255
          ? 'unexceptedError'
          : (error.message.detail[0]?.type ?? 'unexceptedError')
      const errorDetail = error.message.detail[0]?.params as Array<Array<string | number>>
      toasts.push({
        message: t(`errors.${errorType}`, errorDetail),
        severity: 'error',
      })
    } else {
      if (req?.errors) {
        for (const [exchange, err] of Object.entries(req.errors)) {
          const errorType = err[0]
          const params = err.slice(1)
          params.unshift(exchange) // делаем так чтобы params[0] = exchange дабы передавать это в t
          toasts.push({
            message: t(`errors.${errorType}`, params),
            severity: 'error',
          })
        }
      }
      if (req?.success) {
        for (const exchange of req.success) {
          toasts.push({
            message: t(`modal.createSwapOrders.msgs.closedOk`, { ex: exchange }),
            severity: 'success',
          })
        }
      }
      if (req?.empty) {
        for (const exchange of req.empty) {
          toasts.push({
            message: t(`modal.createSwapOrders.msgs.noOrderFound`, { ex: exchange }),
            severity: 'error',
          })
        }
      }
    }
    for (const toast of toasts) {
      _addToast({
        title: profileTitle,
        message: toast.message,
        severity: toast.severity,
      })
    }
  }

  const prepareProfile = async (
    asset: string,
    exBuy: string,
    exSell: string,
    marketType: MarketType,
    leverage: number,
    marginMode: 'isolated' | 'cross',
    profileNum: number,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    const data = {
      ex_buy: exBuy,
      ex_sell: exSell,
      asset: asset,
      sell_asset: sellAsset,
      market_type: marketType,
      margin_mode: marginMode,
      leverage: leverage,
      quote_buy: quoteBuy,
      quote_sell: quoteSell,
    } as PrepareApiRequest
    await tradeStore.prepare(profileNum, data)
    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    let toastSeverity = 'error' as Severities
    let toastMessage = ''
    if (tradeStore.errors.prepare) {
      const errorType =
        tradeStore.errors.prepare.code == -255
          ? 'unexceptedError'
          : (tradeStore.errors.prepare.message.detail[0]?.type ?? 'unexceptedError')

      toastMessage = t(`errors.${errorType}`)
    } else {
      toastMessage = t(`modal.createSwapOrders.profilesPrepared`)
      toastSeverity = 'success'
    }

    if (toastMessage) {
      _addToast({
        title: profileTitle,
        message: toastMessage,
        severity: toastSeverity,
      })
    }
  }

  const prepareSingleProfile = async (
    exchange: string,
    asset: string,
    profileNum: number,
    keyType: ApiKeyType,
    leverage: number,
    marginMode: 'isolated' | 'cross',
    quote: string = DEFAULT_QUOTE,
  ) => {
    await tradeStore.prepareSingleProfile(
      exchange,
      asset,
      profileNum,
      keyType,
      leverage,
      marginMode,
      quote,
    )
    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    let toastSeverity = 'error' as Severities
    let toastMessage = ''
    if (tradeStore.errors.prepareSingleProfile) {
      const errorType =
        tradeStore.errors.prepareSingleProfile.code == -255
          ? 'unexceptedError'
          : (tradeStore.errors.prepareSingleProfile.message.detail[0]?.type ?? 'unexceptedError')

      toastMessage = t(`errors.${errorType}`)
    } else {
      toastMessage = t(`modal.createSwapOrders.profilesPrepared`)
      toastSeverity = 'success'
    }

    if (toastMessage) {
      _addToast({
        title: profileTitle,
        message: toastMessage,
        severity: toastSeverity,
      })
    }
  }

  const closePositions = async (
    exBuy: string,
    exSell: string,
    marketType: MarketType,
    asset: string,
    profileNum: number,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
    priceBuy?: number,
    priceSell?: number,
  ) => {
    const data = {
      asset: asset,
      sell_asset: sellAsset,
      market_type: marketType,
      ex_buy: exBuy,
      ex_sell: exSell,
      quote_buy: quoteBuy,
      quote_sell: quoteSell,
      price_buy: priceBuy,
      price_sell: priceSell,
    } as ClosePositionsRequest
    const req = await tradeStore.closeSwapPositions(profileNum, data)
    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    const toasts = [] as { message: string; severity: Severities }[]
    const error = tradeStore.errors.closeSwapPositions
    if (error) {
      // Если HTTP ошибка
      const errorType =
        error.code == -255
          ? 'unexceptedError'
          : (error.message.detail[0]?.type ?? 'unexceptedError')
      const errorDetail = error.message.detail[0]?.params as Array<Array<string | number>>
      toasts.push({
        message: t(`errors.${errorType}`, errorDetail),
        severity: 'error',
      })
    } else {
      // Обработка ответа
      if (req?.errors) {
        for (const [exchange, err] of Object.entries(req.errors)) {
          const errorType = err[0]
          const params = err.slice(1)
          params.unshift(exchange) // делаем так чтобы params[0] = exchange дабы передавать это в t
          toasts.push({
            message: t(`errors.${errorType}`, params),
            severity: 'error',
          })
        }
      }
      if (req?.success) {
        for (const [exchange, pnl] of Object.entries(req.success)) {
          toasts.push({
            message: t(`modal.createSwapOrders.msgs.closedSuccessfully`, {
              ex: exchange,
              pnl: Number(pnl).toFixed(2),
            }),
            severity: 'success',
          })
        }
      }
      if (req?.empty) {
        for (const exchange of req.empty) {
          toasts.push({
            message: t(`modal.createSwapOrders.msgs.noPositionFound`, { ex: exchange }),
            severity: 'error',
          })
        }
      }
    }
    for (const toast of toasts) {
      _addToast({
        title: profileTitle,
        message: toast.message,
        severity: toast.severity,
      })
    }
  }

  const handleToastsTypes = async (
    title: string,
    asset: string,
    exs: string,
    toasts: { message: string; severity: Severities }[],
  ) => {
    const severities = ['success', 'error'] as Severities[]
    for (const severity of severities) {
      const messages = toasts
        .filter((x) => x.severity == severity)
        .map((x) => x.message)
        .join('\n\n')
      if (messages) {
        sendUserSignal(title, severity, asset.toUpperCase(), exs, messages)
      }
    }
  }

  const createOrders = async (
    asset: string,
    marketType: MarketType,
    amount: number,
    slippage: number,
    leverage: number,
    market: boolean,
    close: boolean,
    reverse: boolean,
    priceBuy: number | null,
    priceSell: number | null,
    exBuy: string,
    exSell: string,
    profileNum: number,
    marginMode: string,
    closeOnFail: boolean,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    const data = {
      price_buy: priceBuy,
      price_sell: priceSell,
      ex_buy: exBuy,
      ex_sell: exSell,
      asset: asset,
      sell_asset: sellAsset,
      market_type: marketType,
      amount: amount,
      leverage: leverage,
      slippage: slippage,
      notify_me: true,
      close: close,
      close_all: false,
      reverse: reverse,
      multi: (profilesMax.value ?? 1) > 1 ? enabledProfiles.value : null,
      market_order: market,
      margin_mode: marginMode,
      close_on_fail: closeOnFail,
      quote_buy: quoteBuy,
      quote_sell: quoteSell,
    } as CreateSwapOrdersRequest

    const req = await tradeStore.createSwapOrders(profileNum, data)
    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    const error = tradeStore.errors.createSwapOrders

    const toasts = [] as { message: string; severity: Severities }[]

    if (error) {
      const errorType =
        error.code == -255
          ? 'unexceptedError'
          : (error.message.detail[0]?.type ?? 'unexceptedError')
      const errorDetail = error.message.detail[0]?.params as Array<Array<string | number>>
      if (errorType == 'create_order_error') {
        // Если ошибка получена в процессе размещения заявки.
        // В этом случае в качестве params приходит список ошибок которые нужно распарсить
        for (const det of errorDetail) {
          const subType = det[0]
          const args = det.slice(1)
          toasts.push({
            message: t(`errors.${subType}`, args),
            severity: 'error',
          })
          // Если ошибка всего одна, необходимо оповестить пользователя
          if (errorDetail.length == 1) {
            toasts.push({
              message: t('modal.createSwapOrders.msgs.checkItByYourself', [args[1]]),
              severity: 'error',
            })
          }
        }
      } else {
        // Если ошибка другого характера
        toasts.push({
          message: t(`errors.${errorType}`, errorDetail),
          severity: 'error',
        })
      }
    } else {
      // Если всё ок
      if (!req?.sell || !req?.buy) {
        toasts.push({
          message: t('internalError'),
          severity: 'error',
        })
      }
      // Если одна из заявок не исполнилась
      else if (req.buy.status != 'closed' || req.sell.status != 'closed') {
        // Если вдруг заявки не исполнились
        const exOrderStatus: Array<[string, CcxtOrderData]> = [
          [exBuy, req.buy],
          [exSell, req.sell],
        ]
        for (const st of exOrderStatus) {
          if (!st[1].amount) {
            continue
          }
          let msg = null
          let success = false
          const tparams = { ex: st[0], perc: 0 }
          if (st[1].status == 'open') {
            const perct = Number((st[1].filled / st[1].amount) * 100)
            if (perct == 0) {
              msg = 'orderNotExecuted'
            } else {
              tparams['perc'] = perct
              msg = 'orderNotFilled'
            }
          } else if (st[1].status == 'closed') {
            success = true
            msg = 'orderClosed'
          } else {
            msg = 'orderProbablyCanceled'
          }
          toasts.push({
            severity: success ? 'success' : 'error',
            message: t(`modal.createSwapOrders.msgs.${msg}`, tparams),
          })
        }
      } else if (!req?.buy.average || !req?.sell.average) {
        // Если по какой то причине нет average
        toasts.push({
          message: t('modal.createSwapOrders.errors.spreadError'),
          severity: 'error',
        })
      } else {
        // Если заявки исполнились
        let realSpread = null

        if (close) {
          // При закрытии
          const spread = calcSpread(req?.buy.average, req?.sell.average, true)
          realSpread = Number(spread).toFixed(2)
        } else {
          // При открытии
          const spread = calcSpread(req?.buy.average, req?.sell.average, false)
          realSpread = Number(spread).toFixed(2)
        }

        toasts.push({
          message: t(
            close
              ? 'modal.createSwapOrders.positionsClosed'
              : 'modal.createSwapOrders.positionsOpened',
            { realSpread: realSpread },
          ),
          severity: 'success',
        })
      }
    }

    // Шлём сообщения в телеграм
    handleToastsTypes(profileTitle, asset, `${exBuy}/${exSell}`.toUpperCase(), toasts)

    for (const toast of toasts) {
      _addToast({
        title: profileTitle,
        message: toast.message,
        severity: toast.severity,
      })
    }
  }

  const createOrderPro = async (
    cex: string,
    asset: string,
    amount: number,
    leverage: number,
    profileNum: number,
    price: number,
    side: string,
    marketType: 'spot' | 'swap',
    isClose: boolean,
    marketOrder: boolean = false,
    marginMode: 'cross' | 'isolated' = 'isolated',
    keyType: ApiKeyType = 'dex_cex',
    quote: string = 'USDT',
  ) => {
    const symbol = buildSymbol(asset, quote, marketType)

    const marketActionId = `${cex}/${symbol}`
    const market = await coreStore.fetchMarket(marketActionId, cex, symbol)
    const contractSize = market?.contractSize ?? 1

    const profileTitle = t('profileTitle', { profileNum: profileNum + 1 })
    const data = {
      exchange: cex,
      amount: amount,
      leverage: leverage,
      price: price,
      asset: asset,
      side: side,
      close: isClose,
      market_order: marketOrder,
      margin_mode: marginMode,
    } as CreateSwapOrderRequest
    const req = await tradeStore.createSwapOrderPro(profileNum, data, keyType)
    const error = tradeStore.errors.createSwapOrderPro
    let toastMessage = ''
    let toastSeverity = 'error' as Severities
    if (error) {
      const errorType =
        error.code == -255
          ? 'unexceptedError'
          : (error.message.detail[0]?.type ?? 'unexceptedError')
      const errorDetail = error.message.detail[0]?.params as Array<Array<string | number>>
      if (errorType == 'create_order_error') {
        // Если ошибка получена в процессе размещения заявки.
        // В этом случае в качестве params приходит список ошибок которые нужно распарсить
        for (const det of errorDetail) {
          const subType = det[0]
          const args = det.slice(1)
          toastMessage = t(`errors.${subType}`, args)
        }
      } else {
        // Если ошибка другого характера
        toastMessage = t(`errors.${errorType}`, errorDetail)
      }
    } else {
      if (!req) {
        toastMessage = t(`errors.unexceptedError`)
      } else {
        const order = req[2]
        if (order.status == 'closed' || order.status == 'canceled') {
          if (!order.filled) {
            toastMessage = t(`orderFailed`, {
              side: side == 'buy' ? t('long') : t('short'),
            })
          } else {
            const baseAmount = order.filled * (contractSize ?? 1)
            toastMessage = t(`orderExecuted`, {
              side: side == 'buy' ? t('long') : t('short'),
              average: order.average,
              usdtAmount: Number((order.average ?? 0) * baseAmount).toFixed(2),
              amount: baseAmount,
            })
            toastSeverity = 'success'
          }
        } else {
          toastMessage = t(`unknownOrderState`)
          console.log(order)
        }
      }
    }

    if (toastMessage) {
      _addToast({
        title: profileTitle,
        message: toastMessage,
        severity: toastSeverity,
      })
      sendUserSignal(
        profileTitle,
        toastSeverity,
        asset.toUpperCase(),
        cex.toUpperCase(),
        toastMessage,
      )
    }

    return toastSeverity
  }

  return {
    createOrderPro,
    createOrders,
    cancelOrders,
    prepareProfile,
    prepareSingleProfile,
    closePositions,
    fetchBalance,
    sendUserSignal,
  }
}
